import React, { useEffect, useContext, useMemo } from 'react';
import first from 'lodash.first';
import delve from 'dlv';

import { PromiseState } from 'react-refetch';

import getPhysicalKPIURL from 'url-constructors/getPhysicalKPIURL';

import salesTypes from 'maps/sales-types';

import connect from 'helpers/apiConnect';
import { EntityContext } from 'context/entityContext';

import OverviewContainer from 'components/Overview/Container/Container';
import PhysicalPerformanceKPIs from '../shared/KPIsData';

import useUrlForTopic from 'components/data-containers/Overview/shared/utils/useUrlForTopic';
import metrics from 'maps/metrics';
import { TimeFrames } from 'components/TopicFilters/shared';

function dataSources(): any {
  return {
    fetchKPIData: (args: any): any => ({
      kpiData: getPhysicalKPIURL(args),
    }),
  };
}

function getLocalSortBy(globalSortBy: TimeFrames): string {
  switch (globalSortBy) {
    case 'lw':
      return 'performance_last_week';
    case 'twtd':
    default:
      return 'count_this_week_to_date';
  }
}

interface IPhysicalPerformanceProps {
  fetchKPIData: Function;
  kpiData: PromiseState<any>;
}

/*
 * The physical performance card shows a KPI based on the topic dropdown
 * and a “Release to date” KPI.
 *
 * Physical is only available for artists and releases
 *
 * Note: as the `sortBy` parameter differs per endpoint, the global sort metric
 * is converted to one supported by this data container.
 */
const PhysicalPerformance: React.FC<IPhysicalPerformanceProps> = (props) => {
  const { fetchKPIData, kpiData = PromiseState.create() } = props;

  const ctx = useContext(EntityContext);
  const {
    entityIds,
    entityType,
    filters,
    viewSettings,
    compareMode,
    customRegionFilter,
  } = ctx;
  const timeframe = viewSettings?.timeframe ?? 'twtd';

  useEffect(() => {
    fetchKPIData({
      entityIds,
      entityType,
      countryIds: filters?.country ?? undefined,
      region: filters?.region,
      compareMode,
      customRegion: customRegionFilter,
    });
  }, [
    entityIds,
    entityType,
    fetchKPIData,
    filters,
    compareMode,
    customRegionFilter,
  ]);

  const isPending = kpiData.pending;

  const sortByValue = getLocalSortBy(timeframe);
  const sortByMetric = metrics[sortByValue];
  const rtdMetric = metrics['count_rtd'];

  const kpis: any = useMemo(() => {
    if (kpiData.fulfilled) {
      const data = kpiData.value;
      return first(data);
    }
  }, [kpiData]);

  const value = delve(kpis, ['data', sortByMetric.value], 0);
  const change = sortByMetric.change
    ? delve(kpis, ['data', sortByMetric.change], 0)
    : 0;
  const rtdValue = delve(kpis, ['data', rtdMetric.value], 0);

  return (
    <OverviewContainer
      filtersToShow={['country', 'region', 'customRegion']}
      title="Physical Performance"
      subtitle={salesTypes.physical.label}
      linkTo={useUrlForTopic('physical')}
    >
      {entityType !== 'release' && <h4>Releases</h4>}
      <PhysicalPerformanceKPIs
        value={value}
        change={change}
        rtdValue={rtdValue}
        title={sortByMetric.label}
        isPending={isPending}
      />
    </OverviewContainer>
  );
};

export default connect(dataSources as any)(PhysicalPerformance as any);
